import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Typography,
  Space,
  message,
  Divider,
  Input,
  Tag,
  Spin,
  Empty,
  Card as AntCard,
  Pagination,
  Tooltip,
} from 'antd';
import {
  MinusCircleOutlined,
  PlusOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { helper } from '@src/controls/controlHelper';
import SingleSelect from '@src/packages/pro-component/schema/SingleSelect';
import ImageModel from '@src/packages/pro-component/schema/Image';
import ArrayImageModel from '@src/packages/pro-component/schema/ArrayImage';
import './BeautyQueenRound.css';

const { Title, Text } = Typography;

const PAGE_ID = 300;

// Định nghĩa interface cho Banner
interface Banner {
  id: number;
  image: string;
  title?: string;
  type: string;
  id_BeautyQueen?: number;
  // Thêm các trường khác nếu cần
}

// Định nghĩa interface cho props của BannerSelector
interface BannerSelectorProps {
  value?: number[];
  onChange?: (value: number[]) => void;
  bannerOptions: Banner[];
}

export default function CreateBeautyQueenRound(props: any) {
  const { onClose } = props;
  const params = props.query || {};
  const { mode, id } = params;
  const [form] = Form.useForm();
  const [pageInfo, setPageInfo] = useState<any>();
  const [, setBeautyQueenRounds] = useState<any[]>([]);
  const [pagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [existingQueens, setExistingQueens] = useState<number[]>([]);
  const [roundSelected, setRoundSelected] = useState<boolean>(false);
  const isUpdate = mode === 'update';
  const [, setRoundOptions] = useState<any[]>([]);
  const [, setQueenOptions] = useState<any[]>([]);
  const [bannerOptions, setBannerOptions] = useState<any[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    const initData = async () => {
      try {
        const _pageInfo = await helper.getPage(PAGE_ID);
        setPageInfo(_pageInfo);

        if (_pageInfo) {
          await Promise.all([
            loadRoundOptions(_pageInfo),
            loadQueenOptions(_pageInfo),
            loadBannerOptions(_pageInfo),
          ]);

          if (isUpdate && id) {
            await getRoundInfo(id, _pageInfo);
          }

          setDataLoaded(true);
        }
      } catch (error) {
        console.log('Error initializing data: ', error);
        message.error('Có lỗi xảy ra khi tải thông tin');
      }
    };

    initData();
  }, []);

  const loadRoundOptions = useCallback(async (_pageInfo: any) => {
    try {
      const response: any = await helper.callPageApi(_pageInfo, 'get-round', {
        select: 'id, name',
        skip: 0,
        limit: pagination.pageSize,
        count: true,
      });
      if (response.status === 200) {
        setRoundOptions(response.data.data);
      }
    } catch (error) {
      console.log('Error loading round options:', error);
    }
  }, []);

  const loadQueenOptions = useCallback(async (_pageInfo: any) => {
    try {
      const response: any = await helper.callPageApi(_pageInfo, 'get-queen', {
        select: 'id, name, image, SBD, city',
        skip: 0,
        limit: pagination.pageSize,
        count: true,
      });
      if (response.status === 200) {
        setQueenOptions(response.data.data);
      }
    } catch (error) {
      console.log('Error loading queen options:', error);
    }
  }, []);

  const loadBannerOptions = useCallback(async (_pageInfo: any) => {
    try {
      const response: any = await helper.callPageApi(
        _pageInfo,
        'find_banner_ads',
        {
          queryInput: JSON.stringify({
            isActive: true,
            type: 'advertisement',
          }),
          skip: 0,
          limit: pagination.pageSize,
          count: true,
        }
      );
      if (response.status === 200) {
        setBannerOptions(response.data.data);
      }
    } catch (error) {
      console.log('Error loading banner options:', error);
      message.error('Có lỗi xảy ra khi tải danh sách banner quảng cáo');
    }
  }, []);

  const getRoundInfo = useCallback(
    async (roundId: number, _pageInfo: any) => {
      try {
        const response: any = await helper.callPageApi(_pageInfo, 'find', {
          queryInput: JSON.stringify({ id: roundId }),
          select:
            'id, beautyQueenId, roundId, thumnail, videos, images, isActive, bannerAds',
          skip: 0,
          limit: pagination.pageSize,
          count: true,
        });

        if (response.status === 200) {
          const data = response.data.data[0];

          let bannerAdsData = [];
          if (data.bannerAds) {
            try {
              if (Array.isArray(data.bannerAds)) {
                bannerAdsData = data.bannerAds;
              } else {
                bannerAdsData = JSON.parse(data.bannerAds);
              }
            } catch (e) {
              console.log('Không thể phân tích dữ liệu bannerAds:', e);
              bannerAdsData = [];
            }
          }

          form.setFieldsValue({
            beautyQueenId: data.beautyQueenId,
            roundId: data.roundId,
            thumnail: data.thumnail,
            videos: data.videos || [],
            images: data.images,
            isActive: data.isActive,
            bannerAds: bannerAdsData,
          });

          setRoundSelected(true);

          await handleRoundChange(data.roundId, _pageInfo);
        } else {
          throw new Error('Failed to fetch round info');
        }
      } catch (error) {
        console.log('Error fetching round info: ', error);
        message.error('Có lỗi xảy ra khi tải thông tin vòng thi');
      }
    },
    [form]
  );

  const handleRoundChange = useCallback(
    async (value: any, _pageInfo?: any) => {
      if (!value) {
        setRoundSelected(false);
        setBeautyQueenRounds([]);
        setExistingQueens([]);
        return;
      }

      try {
        setRoundSelected(true);

        const usePageInfo = _pageInfo || pageInfo;
        const response: any = await helper.callPageApi(usePageInfo, 'find', {
          queryInput: JSON.stringify({ roundId: value }),
          select: 'id, beautyQueen, beautyQueenId, totalPoint, round',
          skip: 0,
          limit: pagination.pageSize,
          count: true,
        });

        if (response.status === 200) {
          setBeautyQueenRounds(response.data.data);
          /* Comment phần cập nhật pagination vì bảng đã bị ẩn
        setPagination({
          ...pagination,
          current: 1,
          total: response.data.count || 0
        });
        */

          const existingQueenIds = response.data.data.map(
            (item: any) => item.beautyQueenId
          );
          setExistingQueens(existingQueenIds);

          if (!isUpdate) {
            const currentQueenId = form.getFieldValue('beautyQueenId');
            if (currentQueenId && existingQueenIds.includes(currentQueenId)) {
              form.setFieldsValue({ beautyQueenId: undefined });
              message.warning(
                'Vui lòng chọn thí sinh khác, thí sinh này đã tồn tại trong vòng thi'
              );
            }
          }
        } else {
          throw new Error('Failed to fetch beauty queen round info');
        }
      } catch (error) {
        console.log('Error fetching beauty queen round info: ', error);
        message.error('Có lỗi xảy ra khi tải thông tin vòng thi');
      }
    },
    [pageInfo, pagination.pageSize, isUpdate, form]
  );

  // Comment hàm handleTableChange vì chỉ được sử dụng cho bảng
  /*
  const handleTableChange = useCallback(async (paginationInfo: any) => {
    const roundId = form.getFieldValue('roundId');
    if (!roundId) return;
    
    try {
      const response: any = await helper.callPageApi(pageInfo, "find", {
        queryInput: JSON.stringify({ roundId }),
        select: "id, beautyQueen, beautyQueenId, totalPoint, round",
        skip: (paginationInfo.current - 1) * paginationInfo.pageSize,
        limit: paginationInfo.pageSize,
        count: true
      });
      
      if (response.status === 200) {
        setBeautyQueenRounds(response.data.data);
        setPagination({
          current: paginationInfo.current,
          pageSize: paginationInfo.pageSize,
          total: response.data.total || 0
        });
        
        const existingQueenIds = response.data.data.map((item: any) => item.beautyQueenId);
        setExistingQueens(prevIds => {
          const newIds = [...prevIds];
          existingQueenIds.forEach((id: number) => {
            if (!newIds.includes(id)) {
              newIds.push(id);
            }
          });
          return newIds;
        });
      } else {
        throw new Error("Failed to fetch beauty queen round info");
      }
    } catch (error) {
      console.log("Error fetching beauty queen round info: ", error);
      message.error("Có lỗi xảy ra khi tải thông tin vòng thi");
    }
  }, [form, pageInfo]);
  */

  // Comment columns vì chỉ dùng cho bảng đã bị comment
  /*
  const columns = [
    {
      title: "Thí sinh",
      dataIndex: ["beautyQueen", "name"],
      key: "name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <div className="contestant-avatar">
            <img
              src={record.beautyQueen.image}
              alt={text}
              className="rounded-circle"
              width={40}
              height={40}
            />
          </div>
          <div className="ms-3">
            <div className="fw-medium">{text}</div>
          </div>
        </div>
      ),
    },
    {
      title: "SBD",
      dataIndex: ["beautyQueen", "SBD"],
      key: "SBD",
    },
    {
      title: "Thành phố",
      dataIndex: ["beautyQueen", "city"],
      key: "city",
    },
    {
      title: "Điểm",
      dataIndex: "totalPoint",
      key: "totalPoint",
    },
  ];
  */

  const validateBeautyQueen = useCallback(
    async (rule: any, value: any) => {
      if (!value) {
        return Promise.reject('Vui lòng chọn thí sinh');
      }

      if (isUpdate && id && value === form.getFieldValue('beautyQueenId')) {
        return Promise.resolve();
      }

      if (existingQueens.includes(value)) {
        return Promise.reject(
          'Thí sinh này đã tồn tại trong vòng thi, vui lòng chọn thí sinh khác'
        );
      }

      return Promise.resolve();
    },
    [existingQueens, isUpdate, id, form]
  );

  const onFinish = useCallback(async () => {
    try {
      const formData = form.getFieldsValue();

      const roundId = formData.roundId;
      const beautyQueenId = formData.beautyQueenId;

      if (!isUpdate) {
        const checkResponse: any = await helper.callPageApi(pageInfo, 'find', {
          queryInput: JSON.stringify({ roundId, beautyQueenId }),
          select: 'id',
          limit: 1,
        });

        if (
          checkResponse.status === 200 &&
          checkResponse.data.data.length > 0
        ) {
          message.error(
            'Thí sinh này đã tồn tại trong vòng thi, vui lòng chọn thí sinh khác'
          );
          return;
        }
      }

      const response: any = isUpdate
        ? await helper.callPageApi(pageInfo, 'update', { id, ...formData })
        : await helper.callPageApi(pageInfo, 'create', formData);

      if (response.status === 200) {
        message.success(
          isUpdate ? 'Cập nhật thành công' : 'Tạo mới thành công'
        );
        form.resetFields();
        onClose();
      } else {
        throw new Error(isUpdate ? 'Failed to update' : 'Failed to create');
      }
    } catch (error: any) {
      console.log('Error creating/updating Beauty Queen Round: ', error);
      message.error('Có lỗi xảy ra');
    }
  }, [pageInfo, isUpdate, id, form, onClose]);

  const handleCancel = () => {
    onClose();
  };

  const CustomRoundSelect = ({ value, onChange, disabled }: any) => {
    return (
      <SingleSelect
        schema={{
          pageId: PAGE_ID,
          api: 'get-round',
          modelSelectMultiple: false,
          modelSelectField: 'id$$ID,name$$Tên vòng thi',
        }}
        placeholder='Chọn vòng thi'
        onChange={(value) => {
          onChange(value);
          handleRoundChange(value);
        }}
        value={value}
        disabled={disabled}
      />
    );
  };

  const CustomQueenSelect = ({ value, onChange, disabled }: any) => {
    return (
      <SingleSelect
        schema={{
          pageId: PAGE_ID,
          api: 'get-queen',
          modelSelectMultiple: false,
          modelSelectField: 'id$$ID,name$$Tên thí sinh',
        }}
        placeholder={
          roundSelected ? 'Chọn thí sinh' : 'Vui lòng chọn vòng thi trước'
        }
        onChange={onChange}
        value={value}
        disabled={disabled || !roundSelected}
      />
    );
  };

  // Cập nhật component BannerSelector với kiểu dữ liệu và phân trang
  const BannerSelector: React.FC<BannerSelectorProps> = ({
    value,
    onChange,
    bannerOptions = [],
  }) => {
    // Đảm bảo value luôn là một mảng
    const selectedValues = Array.isArray(value) ? value : [];

    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10; // Số banner hiển thị mỗi trang

    const handleSelect = (bannerId: number) => {
      const newValue = selectedValues.includes(bannerId)
        ? selectedValues.filter((id) => id !== bannerId)
        : [...selectedValues, bannerId];
      onChange?.(newValue);
    };

    // Lọc banner theo từ khóa tìm kiếm
    const filteredBanners = bannerOptions.filter(
      (banner) =>
        (banner.title || `Banner #${banner.id}`)
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        banner.type.toLowerCase().includes(searchText.toLowerCase())
    );

    // Tính toán số lượng trang
    const totalPages = Math.ceil(filteredBanners.length / pageSize);

    // Lấy danh sách banner cho trang hiện tại
    const currentBanners = filteredBanners.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );

    // Hiển thị số lượng banner đã chọn
    const selectedCount = selectedValues?.length || 0;

    return (
      <div>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Input.Search
            placeholder='Tìm kiếm quảng cáo...'
            style={{ width: 250 }}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1); // Reset về trang 1 khi tìm kiếm
            }}
            allowClear
          />
          <div>
            <Tag color='processing'>{`Đã chọn ${selectedCount}/${bannerOptions.length} quảng cáo`}</Tag>
            {selectedCount > 0 && (
              <Button
                type='link'
                onClick={() => onChange?.([])}
                danger
                size='small'
              >
                Bỏ chọn tất cả
              </Button>
            )}
          </div>
        </div>

        <Row gutter={[16, 16]}>
          {currentBanners.map((banner) => (
            <Col key={banner.id} xs={24} sm={12} md={8} lg={6}>
              <AntCard
                hoverable
                style={{
                  transition: 'all 0.3s',
                  border: '2px solid transparent',
                  height: '100%',
                  ...(selectedValues.includes(banner.id)
                    ? {
                        borderColor: '#1890ff',
                        backgroundColor: 'rgba(24, 144, 255, 0.05)',
                      }
                    : {}),
                }}
                cover={
                  <div
                    style={{
                      height: '120px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <img
                      alt={banner.title || `Banner #${banner.id}`}
                      src={banner.image}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                }
                onClick={() => handleSelect(banner.id)}
                actions={[
                  selectedValues.includes(banner.id) ? (
                    <Button
                      type='text'
                      icon={
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      }
                    >
                      Đã chọn
                    </Button>
                  ) : (
                    <Button type='text' onClick={() => handleSelect(banner.id)}>
                      Chọn
                    </Button>
                  ),
                ]}
                size='small'
              >
                <AntCard.Meta
                  title={
                    <Tooltip title={banner.title || `Banner #${banner.id}`}>
                      <div
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {banner.title || `Banner #${banner.id}`}
                      </div>
                    </Tooltip>
                  }
                />
              </AntCard>
            </Col>
          ))}
        </Row>

        {filteredBanners.length === 0 && (
          <Empty description='Không tìm thấy banner quảng cáo phù hợp' />
        )}

        {totalPages > 1 && (
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Pagination
              current={currentPage}
              total={filteredBanners.length}
              pageSize={pageSize}
              onChange={setCurrentPage}
              showSizeChanger={false}
              size='small'
              showTotal={(total) => `Tổng cộng ${total} banner`}
            />
          </div>
        )}
      </div>
    );
  };

  if (!dataLoaded) {
    return (
      <div className='text-center p-5'>
        <Spin size='large' tip='Đang tải dữ liệu...' />
      </div>
    );
  }

  return (
    <div className='beauty-queen-form-container'>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        className='beauty-queen-form'
      >
        <Card
          className='mb-4 custom-card'
          // title={
          //   <div className="card-gradient-header d-flex justify-content-between align-items-center">
          //     <span>Thông tin Beauty Queen Round</span>
          //     <span className="custom-tag">Mới</span>
          //   </div>
          // }
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name='roundId'
                label='Chọn vòng thi'
                rules={[{ required: true, message: 'Vui lòng chọn vòng thi' }]}
              >
                <CustomRoundSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name='beautyQueenId'
                label='Chọn thí sinh'
                rules={[{ validator: validateBeautyQueen }]}
                tooltip={!roundSelected ? 'Vui lòng chọn vòng thi trước' : ''}
              >
                <CustomQueenSelect />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name='thumnail'
                label={
                  <div>
                    Ảnh đại diện (Thumbnail)
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      Kích thước khuyến nghị: 302x402 px
                    </div>
                  </div>
                }
                rules={[
                  { required: true, message: 'Vui lòng chọn ảnh đại diện' },
                ]}
              >
                <ImageModel />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.List name='videos'>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey, ...restField }) => (
                      <Form.Item
                        {...restField}
                        key={key}
                        name={[name]}
                        fieldKey={[fieldKey]}
                        label={name === 0 ? 'Link Video' : ''}
                        rules={[
                          {
                            required: true,
                            message: 'Vui lòng nhập link video',
                          },
                        ]}
                      >
                        <Input
                          placeholder='Nhập link video'
                          addonAfter={
                            <Button
                              type='link'
                              onClick={() => remove(name)}
                              icon={<MinusCircleOutlined />}
                            />
                          }
                        />
                      </Form.Item>
                    ))}
                    <Form.Item>
                      <Button
                        type='dashed'
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                        disabled={fields.length >= 10}
                      >
                        {fields.length >= 10
                          ? 'Đã đạt giới hạn 10 video'
                          : 'Thêm link video'}
                      </Button>
                      {fields.length > 0 && (
                        <div style={{ marginTop: '8px', textAlign: 'right' }}>
                          <Text type='secondary' style={{ fontSize: '12px' }}>
                            {`${fields.length}/10 video`}
                          </Text>
                        </div>
                      )}
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Col>
          </Row>

          <Divider />

          <Form.Item
            name='images'
            label={
              <div>
                Hình ảnh bổ sung
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  Kích thước khuyến nghị:
                </div>
              </div>
            }
            rules={[
              { required: true, message: 'Vui lòng thêm hình ảnh bổ sung' },
            ]}
          >
            <ArrayImageModel
              imageHeight='270px'
              imageObjectFit='contain'
              rowGutter={[16, 16]}
            />
          </Form.Item>

          <Divider />

          <Form.Item
            name='bannerAds'
            label='Chọn quảng cáo'
            tooltip='Chọn các banner quảng cáo để hiển thị'
          >
            <BannerSelector bannerOptions={bannerOptions} />
          </Form.Item>

          <Divider />

          <Form.Item
            name='isActive'
            valuePropName='checked'
            initialValue={false}
          >
            <Checkbox>Kích hoạt</Checkbox>
          </Form.Item>
        </Card>

        <div
          className='d-flex justify-content-end mt-4'
          style={{ textAlign: 'right', marginTop: 16 }}
        >
          <Space>
            <Button type='primary' htmlType='submit'>
              {isUpdate ? 'Cập nhật thông tin' : 'Lưu thông tin'}
            </Button>
            <Button type='primary' danger onClick={handleCancel}>
              Hủy
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
}
